import { PlanProgress, PlanProgressStep } from '@models/projectPlanDraft.model';

/** Ordered step groups shown in the progress list. PLANNING and DRAFTING_PHASE are one visual group. */
export type PlanProgressRowKey = 'preparing' | 'reading' | 'planning' | 'drafting' | 'validating';

export type PlanProgressRowState = 'done' | 'current' | 'pending';

export interface PlanProgressRow {
  key: PlanProgressRowKey;
  state: PlanProgressRowState;
}

const STEP_ORDER: PlanProgressStep[] = [
  'PREPARING_CONTEXT',
  'READING_ATTACHMENT',
  'PLANNING',
  'DRAFTING_PHASE',
  'VALIDATING',
];

const ROW_ORDER: Record<PlanProgressRowKey, number> = {
  preparing: 0,
  reading: 1,
  planning: 2,
  drafting: 3,
  validating: 4,
};

/**
 * Derives done/current/pending per row from `progress.step`. Rows before the current step are
 * done, the current one is current, later ones are pending. `reading` is omitted when the draft
 * has no attachments to read (and always on revisions, which never resend them).
 */
export function derivePlanProgressRows(
  progress: PlanProgress | null | undefined,
  opts: { hasAttachments: boolean; isRevision: boolean },
): PlanProgressRow[] {
  const showReading = opts.hasAttachments && !opts.isRevision;
  const keys: PlanProgressRowKey[] = ['preparing', ...(showReading ? (['reading'] as const) : []), 'planning', 'drafting', 'validating'];
  const currentIndex = progress ? STEP_ORDER.indexOf(progress.step) : -1;

  return keys.map((key) => {
    const rowOrder = ROW_ORDER[key];
    let state: PlanProgressRowState = 'pending';
    if (progress) {
      if (rowOrder < currentIndex) state = 'done';
      else if (rowOrder === currentIndex) state = 'current';
    }
    return { key, state };
  });
}

/** Phase counts only increase: ignore an out-of-order poll that reports a lower count. */
export function monotonicPhaseCount(previous: number, progress: PlanProgress | null | undefined): number {
  if (progress?.step === 'DRAFTING_PHASE') return Math.max(previous, progress.current);
  return previous;
}

/**
 * The planned phase total N, once the server knows it (a DRAFTING_PHASE write with total > 0,
 * from the model's phase outline). 0 means unknown — the UI then falls back to "Drafting phase k".
 */
export function knownPhaseTotal(previous: number, progress: PlanProgress | null | undefined): number {
  if (progress?.step === 'DRAFTING_PHASE' && progress.total > 0) return progress.total;
  return previous;
}

/** The `aiPlan` locale keys the progress labels use. */
export interface PlanProgressLabels {
  progressPreparing: string;
  progressReading: string;
  progressReadingPending: string;
  progressReadDone: string;
  progressReadDoneOne: string;
  progressPlanning: string;
  progressPlannedPhases: string;
  progressPlannedPhasesOne: string;
  progressDrafting: string;
  progressDraftingOf: string;
  progressDraftingPending: string;
  progressDraftedPhases: string;
  progressDraftedPhasesOne: string;
  progressValidating: string;
  progressStarting: string;
}

/**
 * "Drafting phase k of N" (k clamped to 1..N), "Drafting phase k" when N is unknown (0) but a phase
 * has started, and "Drafting phases" when neither is known yet — never "Drafting phase 1" before
 * any phase exists (walkthrough fix, sprint 38).
 */
export function draftingLabel(t: PlanProgressLabels, k: number, total: number): string {
  if (total > 0) {
    const shown = Math.min(Math.max(k, 1), total);
    return t.progressDraftingOf.replace('{k}', String(shown)).replace('{n}', String(total));
  }
  if (k > 0) return t.progressDrafting.replace('{k}', String(k));
  return t.progressDraftingPending;
}

/**
 * Walkthrough fix (sprint 38). The row label is built from the ROW's own state plus facts that
 * outlive a step (attachment count, phase count/total) — never from the current progress record
 * alone, which is what printed "Reading attachments (0 of )" once the step had moved on.
 */
export function planProgressRowLabel(
  t: PlanProgressLabels,
  row: PlanProgressRow,
  ctx: { progress: PlanProgress | null | undefined; attachmentCount: number; phaseCount: number; phaseTotal: number },
): string {
  switch (row.key) {
    case 'preparing':
      return t.progressPreparing;
    case 'reading': {
      const n = ctx.attachmentCount;
      if (row.state === 'current' && ctx.progress?.step === 'READING_ATTACHMENT') {
        const total = ctx.progress.total || n;
        const i = Math.min(Math.max(ctx.progress.current, 1), total || 1);
        return t.progressReading.replace('{i}', String(i)).replace('{n}', String(total || i));
      }
      if (row.state === 'done' && n > 0) {
        return n === 1 ? t.progressReadDoneOne : t.progressReadDone.replace('{n}', String(n));
      }
      return t.progressReadingPending;
    }
    case 'planning':
      if (row.state === 'done' && ctx.phaseTotal > 0) {
        return ctx.phaseTotal === 1
          ? t.progressPlannedPhasesOne
          : t.progressPlannedPhases.replace('{n}', String(ctx.phaseTotal));
      }
      return t.progressPlanning;
    case 'drafting': {
      if (row.state === 'pending') return t.progressDraftingPending;
      if (row.state === 'done') {
        const n = ctx.phaseTotal || ctx.phaseCount;
        if (n === 1) return t.progressDraftedPhasesOne;
        return n > 1 ? t.progressDraftedPhases.replace('{n}', String(n)) : t.progressDraftingPending;
      }
      return draftingLabel(t, ctx.phaseCount, ctx.phaseTotal);
    }
    default:
      return t.progressValidating;
  }
}

/** Translated one-line label for a progress value; `t` is the `aiPlan` locale section. */
export function planProgressLabel(t: PlanProgressLabels, progress: PlanProgress | null | undefined): string {
  if (!progress) return t.progressStarting;
  switch (progress.step) {
    case 'PREPARING_CONTEXT':
      return t.progressPreparing;
    case 'READING_ATTACHMENT':
      return t.progressReading.replace('{i}', String(progress.current)).replace('{n}', String(progress.total));
    case 'PLANNING':
      return t.progressPlanning;
    case 'DRAFTING_PHASE':
      return draftingLabel(t, progress.current, progress.total);
    case 'VALIDATING':
      return t.progressValidating;
    default:
      return progress.label || t.progressStarting;
  }
}

/** Where a resumable draft reopens, by its target. */
export function draftResumePath(draft: { draftId: string; projectId?: string; purpose?: string | null }): string {
  if (draft.projectId) return `/projects/${draft.projectId}/plan/ai?draft=${draft.draftId}`;
  if (draft.purpose === 'TEMPLATE') return `/templates/generate?draft=${draft.draftId}`;
  return `/projects/new?draft=${draft.draftId}`;
}
